import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthSessionService } from '../services/session/auth-session.service';

const authEndpointPaths = ['/auth/obtain-tokens', '/auth/refresh-tokens'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const session = inject(AuthSessionService);
  const isAuthRequest = authEndpointPaths.some((path) => req.url.includes(path));

  if (isAuthRequest) {
    return next(req);
  }

  const accessToken = session.getAccessToken();
  const refreshToken = session.getRefreshToken();

  if (!accessToken && refreshToken) {
    return session.refreshTokens().pipe(
      switchMap((tokens) =>
        next(addAuthorizationHeader(req, tokens.access_token)),
      ),
      catchError((error) => handleAuthFailure(error, session, router)),
    );
  }

  if (!accessToken) {
    return next(req);
  }

  if (session.shouldRefreshAccessToken()) {
    return session.refreshTokens().pipe(
      switchMap((tokens) =>
        next(addAuthorizationHeader(req, tokens.access_token)),
      ),
      catchError((error) => handleAuthFailure(error, session, router)),
    );
  }

  return next(addAuthorizationHeader(req, accessToken)).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (!refreshToken) {
        return handleAuthFailure(error, session, router);
      }

      return session.refreshTokens().pipe(
        switchMap((tokens) =>
          next(addAuthorizationHeader(req, tokens.access_token)),
        ),
        catchError((refreshError) =>
          handleAuthFailure(refreshError, session, router),
        ),
      );
    }),
  );
};

function addAuthorizationHeader(
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function handleAuthFailure(
  error: unknown,
  session: AuthSessionService,
  router: Router,
) {
  session.clear();
  void router.navigateByUrl('/admin');
  return throwError(() => error);
}
